// Wraps 3D FPSGame + HUD overlays + Pause/End screens + round intermission.
import { useEffect, useState } from "react";
import { useGameStore, SCREEN } from "@/stores/gameStore";
import { useMatchStore } from "@/stores/matchStore";
import { useSettings } from "@/stores/settingsStore";
import { getAgent, agentForCode } from "@/game/agents/agents";
import { getWeapon } from "@/game/weapons/weapons";
import { MATCH } from "@/constants/testIds";
import { Audio_ } from "@/lib/audio";
import FPSGame from "@/game/FPSGame";
import GameHUD from "@/hud/HUD";
import Minimap from "@/hud/Minimap";
import PauseMenu from "@/hud/PauseMenu";
import MatchEndScreen from "@/hud/MatchEndScreen";

export default function MatchScreen() {
    const config = useGameStore((s) => s.matchConfig);
    const goto = useGameStore((s) => s.goto);
    const phase = useMatchStore((s) => s.phase);
    const setConfig = useMatchStore((s) => s.setConfig);
    const nextRound = useMatchStore((s) => s.nextRound);
    const togglePause = useMatchStore((s) => s.togglePause);

    const [ready, setReady] = useState(false);
    const [showBriefing, setShowBriefing] = useState(true);

    useEffect(() => {
        if (!config) {
            goto(SCREEN.PLAY);
            return;
        }
        setConfig(config);
        // Small delay so the canvas can mount cleanly
        const t = setTimeout(() => setReady(true), 200);
        return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [config]);

    // Combat ambience — start on mount, stop on unmount (or match_end)
    useEffect(() => {
        Audio_.startCombatAmbience?.();
        return () => Audio_.stopCombatAmbience?.();
    }, []);

    // Cut the ambience the moment the match ends so victory/defeat cues land clean
    useEffect(() => {
        if (phase === "match_end") Audio_.stopCombatAmbience?.();
    }, [phase]);

    // Round-end intermission: auto-continue after ~2.5s
    useEffect(() => {
        if (phase !== "round_end") return;
        const t = setTimeout(() => nextRound(), 2500);
        return () => clearTimeout(t);
    }, [phase, nextRound]);

    if (!config) return null;

    return (
        <div data-testid={MATCH.root} className="fixed inset-0 bg-black overflow-hidden">
            {ready && (
                <FPSGame
                    config={config}
                    onPause={() => togglePause()}
                />
            )}

            {phase !== "match_end" && <GameHUD />}
            {phase === "playing" && <Minimap />}

            {phase === "paused" && (
                <PauseMenu
                    onResume={() => {
                        togglePause();
                        // Re-lock pointer
                        setTimeout(() => {
                            const canvas = document.querySelector("canvas");
                            canvas?.requestPointerLock?.();
                        }, 50);
                    }}
                />
            )}

            {phase === "round_end" && <RoundEndBanner />}
            {phase === "match_end" && <MatchEndScreen config={config} />}

            {showBriefing && phase === "playing" && (
                <StartBriefing config={config} onDismiss={() => setShowBriefing(false)} />
            )}
        </div>
    );
}

function RoundEndBanner() {
    const result = useMatchStore((s) => s.lastRoundResult);
    const round = useMatchStore((s) => s.round);
    return (
        <div className="fixed inset-0 z-30 flex items-center justify-center bg-black/40 backdrop-blur-sm axis-fade-in pointer-events-none">
            <div className="axis-glass axis-cut px-12 py-6 text-center">
                <div className="axis-mono text-[10px] tracking-[0.5em] text-white/50">ROUND {round}</div>
                <div
                    className="axis-heading text-5xl tracking-[0.3em] mt-1"
                    style={{ color: result === "won" ? "#00e5ff" : "#ff2a2a" }}
                >
                    {result === "won" ? "ROUND WON" : "ROUND LOST"}
                </div>
                <div className="axis-mono text-[10px] tracking-[0.4em] text-white/40 mt-2">
                    NEXT ROUND STARTING...
                </div>
            </div>
        </div>
    );
}

function StartBriefing({ config, onDismiss }) {
    const selectedAgent = useSettings((s) => s.selectedAgent) || "recruit";
    const partySlots = useSettings((s) => s.partySlots) || [null, null];
    const activeWeapon = useMatchStore((s) => s.activeWeapon);

    const me = getAgent(selectedAgent);
    const myWeapon = getWeapon(activeWeapon);
    const teamSizeN = parseInt(String(config.teamSize).split("v")[0], 10) || 1;
    const slotCount = Math.max(0, teamSizeN - 1);
    // Pad party slots up to full squad size so OPEN SLOT tiles match the badge.
    const teammates = Array.from({ length: slotCount }).map((_, i) => {
        const slot = partySlots[i];
        if (!slot?.code) return null;
        return { code: slot.code, agent: agentForCode(slot.code) };
    });
    const teamCount = 1 + teammates.filter(Boolean).length;

    useEffect(() => {
        const t = setTimeout(onDismiss, 4200);
        // Try to auto lock pointer after brief moment
        const lockT = setTimeout(() => {
            const canvas = document.querySelector("canvas");
            canvas?.requestPointerLock?.();
        }, 1500);
        const onKey = (e) => {
            if (e.code === "Space" || e.code === "Enter") onDismiss();
        };
        window.addEventListener("keydown", onKey);
        return () => {
            clearTimeout(t);
            clearTimeout(lockT);
            window.removeEventListener("keydown", onKey);
        };
    }, [onDismiss]);

    return (
        <div
            data-testid="match-briefing"
            className="fixed inset-x-0 top-20 z-20 flex flex-col items-center pointer-events-none axis-fade-in"
        >
            <div className="axis-glass axis-cut px-8 py-4 text-center max-w-xl">
                <div className="axis-mono text-[10px] tracking-[0.5em] text-cyan-300/80">MISSION BRIEFING</div>
                <div className="axis-heading text-2xl tracking-widest text-white mt-1">
                    ELIMINATE ALL ENEMIES
                </div>
                <div className="axis-mono text-[11px] tracking-[0.3em] text-white/50 mt-2">
                    MAP · {config.mapId.replace("_", " ").toUpperCase()} &nbsp;·&nbsp; TEAM · {config.teamSize} &nbsp;·&nbsp; AI · {config.difficulty.toUpperCase()}
                </div>
                <div className="axis-mono text-[10px] tracking-[0.4em] text-cyan-300/70 mt-3">
                    LMB FIRE · RMB AIM · WASD MOVE · R RELOAD · 1/2/3 SWAP · ESC PAUSE
                </div>
            </div>

            {/* Team loadout preview */}
            <div
                data-testid="match-loadout-preview"
                className="mt-4 axis-glass axis-cut px-5 py-3 flex items-center gap-4 max-w-2xl"
            >
                <div className="axis-mono text-[9px] tracking-[0.4em] text-white/40 pr-3 border-r border-white/10">
                    SQUAD<br/>
                    <span className="text-cyan-300/80">{teamCount}/{teamSizeN}</span>
                </div>
                <LoadoutSlot label="YOU" agent={me} weapon={myWeapon} tint="#00e5ff" />
                {teammates.map((tm, i) => (
                    <LoadoutSlot
                        key={i}
                        label={`SLOT ${i + 1}`}
                        agent={tm?.agent}
                        weapon={tm ? myWeapon : null}
                        code={tm?.code}
                        empty={!tm}
                        tint={tm ? "#ff9a4a" : "rgba(255,255,255,0.2)"}
                    />
                ))}
            </div>
        </div>
    );
}

function LoadoutSlot({ label, agent, weapon, code, empty, tint }) {
    return (
        <div
            data-testid={`loadout-slot-${label.toLowerCase().replace(/\s/g, "-")}`}
            className="flex items-center gap-3 min-w-[190px]"
        >
            <div
                className="w-9 h-9 axis-cut-sm border flex items-center justify-center flex-shrink-0"
                style={{
                    borderColor: tint,
                    background: empty ? "transparent" : `${agent?.palette?.vest || "#222"}`,
                }}
            >
                {empty ? (
                    <span className="axis-mono text-[9px] text-white/40">—</span>
                ) : (
                    <div
                        className="w-4 h-4 rounded-full"
                        style={{ background: agent.palette.skin, border: `1px solid ${agent.palette.metal}` }}
                    />
                )}
            </div>
            <div className="min-w-0">
                <div className="axis-mono text-[9px] tracking-[0.4em]" style={{ color: tint }}>
                    {label}
                </div>
                {empty ? (
                    <div className="axis-heading text-[13px] tracking-widest text-white/40">
                        OPEN SLOT
                    </div>
                ) : (
                    <>
                        <div className="axis-heading text-[13px] tracking-widest text-white truncate">
                            {agent.name}
                            {code && (
                                <span className="ml-2 axis-mono text-[9px] text-white/40">#{code}</span>
                            )}
                        </div>
                        {weapon && (
                            <div
                                className="axis-mono text-[9px] tracking-[0.3em] truncate"
                                style={{ color: weapon.model.sightColor }}
                            >
                                {weapon.displayName}
                            </div>
                        )}
                    </>
                )}
            </div>
        </div>
    );
}
